import { Link, NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { useAuth } from "@/lib/auth";
import { useTheme } from "@/lib/theme";
import MobileBottomNav from "@/components/MobileBottomNav";
import {
  House, ChartLineUp, BookOpen, Gear, SignOut, MoonStars, Sun, Plus, Calendar, List, X, FolderOpen, Stack, Sparkle, Pulse, Lightning, ListChecks, Scales, TrendUp, Users, ShieldStar, FileText, CreditCard, Handshake,
} from "@phosphor-icons/react";
import AIAnalystPanel from "@/components/AIAnalystPanel";
import Tour from "@/components/Tour";
import Logo from "@/components/Logo";

const navItems = [
  { to: "/dashboard", label: "Dashboard", icon: House, testid: "nav-dashboard" },
  { to: "/workspaces", label: "Workspaces", icon: FolderOpen, testid: "nav-workspaces" },
  { to: "/workflows", label: "Workflow Runs", icon: ListChecks, testid: "nav-workflows" },
  { to: "/financial-health", label: "Financial Health", icon: Scales, testid: "nav-financial-health" },
  { to: "/investment-readiness", label: "Readiness", icon: TrendUp, testid: "nav-readiness" },
  { to: "/investment-docs", label: "AI Investment Documentation", icon: FileText, testid: "nav-investment-docs" },
  { to: "/radar", label: "Radar", icon: Pulse, testid: "nav-radar" },
  { to: "/builder", label: "Builder", icon: Stack, testid: "nav-builder" },
  { to: "/assessments", label: "Dossiers", icon: ChartLineUp, testid: "nav-assessments" },
  { to: "/knowledge", label: "Research Vault", icon: BookOpen, testid: "nav-knowledge" },
  { to: "/labs", label: "Labs", icon: Lightning, testid: "nav-labs" },
  { to: "/marketplace", label: "Marketplace", icon: Handshake, testid: "nav-marketplace", badge: "Soon" },
  { to: "/wallet", label: "Credit Wallet", icon: CreditCard, testid: "nav-wallet" },
  { to: "/billing", label: "Billing", icon: CreditCard, testid: "nav-billing" },
  { to: "/settings", label: "Settings", icon: Gear, testid: "nav-settings" },
];

const adminNavItems = [
  { to: "/admin", label: "Admin", icon: ShieldStar, testid: "nav-admin" },
  { to: "/admin/revenue", label: "Revenue Readiness", icon: TrendUp, testid: "nav-revenue" },
  { to: "/admin/waitlist", label: "Waitlist", icon: Users, testid: "nav-admin-waitlist" },
  { to: "/crm", label: "CRM Pipeline", icon: Users, testid: "nav-crm" },
];

const superAdminNavItems = [
  { to: "/mission-control", label: "Mission Control", icon: ShieldStar, testid: "nav-mission-control" },
];

export default function AppLayout() {
  const { user, org, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [analystOpen, setAnalystOpen] = useState(false);
  const isAdmin = user?.role === "super_admin" || user?.role === "admin";
  const isSuperAdmin = user?.role === "super_admin";

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  // Detect workspace id from URL → enables workspace-aware analyst context
  const wsMatch = location.pathname.match(/^\/workspaces\/([^/]+)/);
  const inferredWorkspaceId = wsMatch ? wsMatch[1] : null;

  const SidebarContent = (
    <>
      <div className="px-6 pt-6 pb-8 border-b border-border">
        {org?.logo_url ? (
          <Link to="/dashboard" className="flex items-center gap-2.5" data-testid="sidebar-logo">
            <img src={org.logo_url.startsWith('http') ? org.logo_url : `${process.env.REACT_APP_BACKEND_URL}${org.logo_url}`} alt="logo" className="h-9 w-9 object-contain rounded-md" />
            <div className="font-display text-lg leading-tight tracking-tight">{org?.name || "ORIXA"}</div>
          </Link>
        ) : (
          <Logo variant="wordmark" size={36} to="/dashboard" testId="sidebar-logo" />
        )}
      </div>

      <button
        onClick={() => navigate("/assessments/new")}
        className="mx-4 mt-4 mb-2 inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground rounded-lg py-3 text-sm font-semibold hover:bg-primary/90 transition-colors card-elev"
        data-testid="sidebar-new-assessment"
      >
        <Plus weight="bold" size={16} /> New mandate
      </button>

      <nav className="flex-1 px-3 py-4 space-y-1">
        {navItems.map((it) => (
          <NavLink
            key={it.to}
            to={it.to}
            data-testid={it.testid}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 text-sm transition-colors rounded-lg ${
                isActive ? "bg-teal-soft text-teal font-semibold" : "text-muted-foreground hover:bg-secondary hover:text-foreground"
              }`
            }
          >
            <it.icon size={18} weight="regular" />
            <span className="flex-1">{it.label}</span>
            {it.badge && (
              <span className="text-[9px] font-bold uppercase tracking-wider bg-gold-soft text-gold rounded-full px-1.5 py-0.5">{it.badge}</span>
            )}
          </NavLink>
        ))}
        {isAdmin && (
          <>
            <div className="px-3 pt-4 pb-1 text-[10px] uppercase tracking-[0.18em] font-semibold text-muted-foreground">Administration</div>
            {adminNavItems.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                data-testid={it.testid}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 text-sm transition-colors rounded-lg ${
                    isActive ? "bg-teal-soft text-teal font-semibold" : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                  }`
                }
              >
                <it.icon size={18} weight="regular" />
                {it.label}
              </NavLink>
            ))}
          </>
        )}
        {isSuperAdmin && (
          <>
            <div className="px-3 pt-4 pb-1 text-[10px] uppercase tracking-[0.18em] font-semibold text-muted-foreground">Mission Control</div>
            {superAdminNavItems.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                data-testid={it.testid}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 text-sm transition-colors rounded-lg ${
                    isActive ? "bg-teal-soft text-teal font-semibold" : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                  }`
                }
              >
                <it.icon size={18} weight="regular" />
                {it.label}
              </NavLink>
            ))}
          </>
        )}
      </nav>

      <div className="border-t border-border p-3 space-y-1">
        <a
          href="https://cal.com/romuald-ouattara-wttutm/call-orixa-invest"
          target="_blank" rel="noopener noreferrer"
          className="flex items-center gap-3 px-3 py-2.5 text-sm text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors rounded-lg"
          data-testid="sidebar-book-call"
        >
          <Calendar size={18} /> Book a product demo
        </a>
        <button
          onClick={toggle}
          className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors rounded-lg"
          data-testid="theme-toggle"
        >
          {theme === "dark" ? <Sun size={18} /> : <MoonStars size={18} />}
          {theme === "dark" ? "Light mode" : "Dark mode"}
        </button>
        <div className="px-2 pt-2 pb-1 flex items-center gap-3 text-[11px] text-muted-foreground">
          <Link to="/legal/terms" target="_blank" className="hover:text-foreground" data-testid="sidebar-legal-terms">Terms</Link>
          <span className="text-border">·</span>
          <Link to="/legal/privacy" target="_blank" className="hover:text-foreground" data-testid="sidebar-legal-privacy">Privacy</Link>
          <span className="text-border">·</span>
          <Link to="/legal/refund" target="_blank" className="hover:text-foreground" data-testid="sidebar-legal-refund">Refund</Link>
        </div>
        <div className="px-2 pt-3 mt-1 border-t border-border">
          <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-muted-foreground mb-2 truncate">{org?.name}</div>
          {user?.is_demo && user?.role !== "super_admin" && user?.role !== "admin" && (
            <div
              className="mb-2 inline-flex items-center gap-1.5 rounded-full bg-gold-soft text-gold border border-gold/30 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.14em]"
              data-testid="sidebar-demo-badge"
              title="Shared demo account · Sample data only"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-current" />
              Professional Demo
            </div>
          )}
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-primary to-[hsl(var(--info))] text-white grid place-items-center text-sm font-bold">
              {user?.full_name?.[0]?.toUpperCase() || "U"}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm truncate font-medium" data-testid="sidebar-user-name">{user?.full_name}</div>
              <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
            </div>
            <button onClick={logout} title="Sign out" data-testid="logout-button" className="text-muted-foreground hover:text-destructive transition-colors p-1.5">
              <SignOut size={18} />
            </button>
          </div>
        </div>
      </div>
    </>
  );

  return (
    <div className="min-h-screen flex bg-background text-foreground">
      {/* Desktop Sidebar */}
      <aside className="hidden md:flex w-64 lg:w-72 border-r border-border bg-card flex-col" data-testid="app-sidebar">
        {SidebarContent}
      </aside>

      {/* Mobile Top Bar */}
      <header className="md:hidden fixed top-0 inset-x-0 z-30 h-14 bg-card border-b border-border flex items-center justify-between px-4">
        <button onClick={() => setMobileOpen(true)} className="text-foreground p-1.5" aria-label="Open menu" data-testid="mobile-menu-open">
          <List size={22} weight="bold" />
        </button>
        <Link to="/dashboard" className="flex items-center gap-2" data-testid="mobile-logo">
          {org?.logo_url ? (
            <img src={org.logo_url.startsWith('http') ? org.logo_url : `${process.env.REACT_APP_BACKEND_URL}${org.logo_url}`} alt="" className="h-6 w-6 object-contain rounded" />
          ) : (
            <Logo variant="symbol" size={28} as="div" />
          )}
          <span className="font-display text-base tracking-[0.18em]">ORIXA</span>
        </Link>
        <button onClick={toggle} className="text-foreground p-1.5" aria-label="Toggle theme" data-testid="mobile-theme-toggle">
          {theme === "dark" ? <Sun size={18} /> : <MoonStars size={18} />}
        </button>
      </header>

      {/* Mobile Drawer */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-foreground/40" onClick={() => setMobileOpen(false)} data-testid="mobile-drawer-backdrop" />
          <div className="relative w-72 max-w-[85vw] h-full bg-card flex flex-col animate-in slide-in-from-left duration-200" data-testid="mobile-drawer">
            <button onClick={() => setMobileOpen(false)} className="absolute top-4 right-4 p-1.5 text-muted-foreground hover:text-foreground" aria-label="Close menu" data-testid="mobile-drawer-close">
              <X size={20} weight="bold" />
            </button>
            {SidebarContent}
          </div>
        </div>
      )}

      {/* Main */}
      <main className="flex-1 overflow-y-auto pt-14 pb-20 md:pt-0 md:pb-0">
        <Outlet />
      </main>

      <MobileBottomNav />

      {/* AI Analyst floating launcher */}
      {!analystOpen && (
        <button
          onClick={() => setAnalystOpen(true)}
          className="fixed bottom-20 md:bottom-6 right-4 md:right-6 z-40 bg-primary text-primary-foreground rounded-full pl-4 pr-5 py-3 shadow-2xl hover:bg-primary/90 transition-all hover:scale-105 inline-flex items-center gap-2"
          data-testid="ai-analyst-launcher"
          aria-label="Ask the Orixa AI Analyst"
        >
          <Sparkle size={16} weight="duotone" />
          <span className="text-sm font-semibold hidden sm:inline">Ask the Analyst</span>
        </button>
      )}
      {analystOpen && (
        <AIAnalystPanel
          workspaceId={inferredWorkspaceId}
          onClose={() => setAnalystOpen(false)}
        />
      )}
      <Tour />
    </div>
  );
}
